import { useCallback, useEffect, useState } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import { Button, Icon, Spinner } from '@ng-unifi/ui';
import { useAuth } from '../auth/AuthContext.js';
import { apiClient, ApiError } from '../lib/api.js';

interface PasskeyRow {
  id: string;
  nickname: string | null;
  transports: string[];
  device_type: string | null;
  backed_up: boolean;
  created_at: string;
  last_used_at: string | null;
}

export function AccountSecurityPage() {
  const { state } = useAuth();
  const [rows, setRows] = useState<PasskeyRow[] | null>(null);
  const [nickname, setNickname] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await apiClient.get<{ data: PasskeyRow[] }>('/api/v1/auth/passkeys', {
        skipTenantScope: true,
      });
      setRows(res.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load passkeys');
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function addPasskey(attachment?: 'platform' | 'cross-platform') {
    setBusy(attachment ?? 'default');
    setError(null);
    setToast(null);
    try {
      const start = await apiClient.post<{
        data: { options: Parameters<typeof startRegistration>[0]['optionsJSON'] };
      }>('/api/v1/auth/passkeys/register/start', attachment ? { attachment } : {}, {
        skipTenantScope: true,
      });
      const credential = await startRegistration({ optionsJSON: start.data.options });
      await apiClient.post(
        '/api/v1/auth/passkeys/register/complete',
        { credential, nickname: nickname.trim() || undefined },
        { skipTenantScope: true },
      );
      setNickname('');
      setToast('Passkey added.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add passkey');
    } finally {
      setBusy(null);
    }
  }

  async function removePasskey(row: PasskeyRow) {
    setBusy(row.id);
    setError(null);
    setToast(null);
    try {
      await apiClient.post(
        '/api/v1/auth/passkeys/remove',
        { id: row.id },
        { skipTenantScope: true },
      );
      setToast('Passkey removed.');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove passkey');
    } finally {
      setBusy(null);
    }
  }

  const mfaEnrolled = state.status === 'authed' && state.user.mfa_enrolled;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex items-center gap-4">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary-container text-on-primary">
            <Icon name="shield" className="text-2xl" />
          </div>
          <div>
            <h1 className="font-headline-md text-headline-md text-on-surface">Account security</h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Password, passkeys, and optional authenticator fallback for your portal sign-in.
            </p>
          </div>
        </div>
      </header>

      {toast ? (
        <div
          role="status"
          className="flex items-center gap-2 rounded-lg bg-secondary-container px-4 py-2 font-body-md text-body-md text-on-secondary-container"
        >
          <Icon name="check_circle" />
          <span>{toast}</span>
        </div>
      ) : null}
      {error ? (
        <div
          role="alert"
          className="rounded-lg bg-error-container px-4 py-2 font-body-md text-body-md text-on-error-container"
        >
          {error}
        </div>
      ) : null}

      <section className="space-y-4 rounded-xl border border-outline-variant bg-surface-container-lowest p-5">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-title-lg text-title-lg text-on-surface">Authenticator fallback</h2>
            <p className="font-body-md text-body-md text-on-surface-variant">
              {mfaEnrolled ? 'TOTP enrolled' : 'TOTP fallback not enrolled'}
            </p>
          </div>
          <span
            className={`inline-flex w-fit items-center gap-1 rounded-full px-3 py-1 font-label-md text-label-md ${
              mfaEnrolled
                ? 'bg-secondary-container text-on-secondary-container'
                : 'bg-error-container text-on-error-container'
            }`}
          >
            <Icon name={mfaEnrolled ? 'check_circle' : 'warning'} className="text-sm" />
            {mfaEnrolled ? 'Enrolled' : 'Missing'}
          </span>
        </div>
      </section>

      <section className="space-y-4 rounded-xl border border-outline-variant bg-surface-container-lowest p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="font-title-lg text-title-lg text-on-surface">Passkeys</h2>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Face ID, fingerprint, device PIN, or hardware security key.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              maxLength={100}
              className="h-10 rounded-lg border border-outline-variant bg-surface px-3 font-body-md text-body-md outline-none focus:ring-2 focus:ring-primary"
              placeholder="Passkey name"
              disabled={busy !== null}
            />
            <Button
              type="button"
              onClick={() => void addPasskey()}
              loading={busy === 'default'}
              disabled={busy !== null}
            >
              <Icon name="fingerprint" />
              Add
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => void addPasskey('platform')}
            loading={busy === 'platform'}
            disabled={busy !== null}
          >
            <Icon name="devices" />
            This device
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void addPasskey('cross-platform')}
            loading={busy === 'cross-platform'}
            disabled={busy !== null}
          >
            <Icon name="phone_iphone" />
            Phone or key
          </Button>
        </div>

        {rows == null ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-outline-variant p-5 text-center font-body-md text-body-md text-on-surface-variant">
            No passkeys enrolled.
          </div>
        ) : (
          <div className="divide-y divide-outline-variant overflow-hidden rounded-lg border border-outline-variant">
            {rows.map((row) => (
              <div
                key={row.id}
                className="flex flex-col gap-3 bg-surface px-4 py-3 md:flex-row md:items-center md:justify-between"
              >
                <div className="min-w-0">
                  <div className="font-body-md text-body-md font-medium text-on-surface">
                    {row.nickname || 'Passkey'}
                  </div>
                  <div className="font-body-sm text-body-sm text-on-surface-variant">
                    {row.device_type ?? 'unknown'} · added{' '}
                    {new Date(row.created_at).toLocaleDateString()}
                    {row.last_used_at
                      ? ` · last used ${new Date(row.last_used_at).toLocaleDateString()}`
                      : ''}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => void removePasskey(row)}
                  loading={busy === row.id}
                  disabled={busy !== null}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
